import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import API from '../../../utils/constant';
import {
  Badge, Button, ConfirmDialog, EmptyState, ErrorPanel, PageHeader, PagedFooter, PanelCard, PillTabs, ShowMoreFooter,
  Spinner, StatStrip, Track,
} from '../../components/ui';
import usePagination from '../../lib/usePagination';
import { personName } from '../../lib/names';
import { BarChart3, Check, Download, FileCheck, Upload } from 'lucide-react';
import { SchoolApi } from '../../api/client';
import { apiErrorText } from '../../lib/apiError';
import { sheetFilename } from './PaperExamsPage';
import {
  useRollNumbers, useStudentsList, useSheets, useSheetsUpload, useSheetsConfirmMatched,
} from '../../api/hooks';
import type { SheetRow, SheetStatus } from '../../api/types';
import DropZone from './components/DropZone';
import ScansTable from './components/ScansTable';
import ScanDrawer from './components/ScanDrawer';
import MissingCard from './components/MissingCard';
import { warningLabel } from './components/scanWarnings';
import { countSheets, isReviewable, missingStudents, type RosterStudent } from './sheetStats';
import { plural } from '../../lib/plural';
import { timeOf } from '../../lib/dates';

// Requesting every status (including 'discarded', which the backend leaves out
// of the default list and out of `counts`) so the page can filter and count
// client-side from one fetch instead of a query per tab.
const ALL_STATUSES: SheetStatus[] = [
  'queued', 'reading', 'matched', 'needs_review', 'duplicate', 'error', 'confirmed', 'discarded',
];

type FilterKey = 'all' | 'needs_review' | 'duplicate' | 'error' | 'matched' | 'queued' | 'confirmed' | 'discarded';

function matchesFilter(row: SheetRow, filter: FilterKey): boolean {
  if (filter === 'all') return true;
  if (filter === 'queued') return row.status === 'queued' || row.status === 'reading';
  return row.status === filter;
}

/** Review queue pill (design `.pill`): review / duplicate / error. */
const KIND_LABEL: Partial<Record<SheetStatus, string>> = { needs_review: 'Review', duplicate: 'Duplicate', error: 'Error' };
const KIND_TONE = { needs_review: 'accent', duplicate: 'warning', error: 'danger' } as const;
/** Sheets listed in the review queue before "Show all". */
const QUEUE_PREVIEW = 5;

// Legacy exam-detail response (api/views.py GetExamDetailData) — untyped
// upstream; only the fields used for the header are named here. Reusing this
// endpoint (already used by ExamDetailComponent) is cheaper than adding a new
// school_panel endpoint just for a subject/grade line.
interface LegacyExamDetail {
  subject?: string | null;
  subject_name?: string | null;
  school_subject?: { name?: string | null } | null;
  grade?: string | null;
  grade_name?: string | null;
  school_grade_name?: string | null;
  school_grade?: { name?: string | null } | null;
  // Used by ScanDrawer's student picker (useStudentsList({grade})) — the only
  // field here this legacy endpoint names by id rather than by display text.
  school_grade_id?: number | null;
  // Who sits the exam: everyone in the grade, the assigned sections, or an assigned list of students.
  assignment_scope?: string | null;
  assigned_student_ids?: number[] | null;
  assigned_section_ids?: number[] | null;
}

function useLegacyExamDetail(examId: number | null) {
  return useQuery({
    queryKey: ['school', 'exam-detail-legacy', examId],
    queryFn: () => API.get<LegacyExamDetail>(`/api/school/exam-detail/${examId}/`).then((r) => r.data),
    enabled: examId != null,
    staleTime: 5 * 60 * 1000,
  });
}



function formatQueuedAt(iso: string): string {
  return timeOf(iso);
}

/** "10 · A", "10 · A and 10 · B", "10 · A, 10 · B and 10 · C". */
function joinNames(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}


export default function SheetsPage() {
  const navigate = useNavigate();
  const { examId: examIdParam } = useParams<{ examId: string }>();
  const parsed = Number(examIdParam);
  const examId = Number.isFinite(parsed) && parsed > 0 ? parsed : null;

  const sheetsQ = useSheets(examId, ALL_STATUSES.join(','));
  const examDetailQ = useLegacyExamDetail(examId);
  const uploadMut = useSheetsUpload(examId ?? 0);
  const confirmMatchedMut = useSheetsConfirmMatched(examId ?? 0);

  const [filter, setFilter] = useState<FilterKey>('all');
  const [section, setSection] = useState('all');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [queueAll, setQueueAll] = useState(false);
  const [confirmingAll, setConfirmingAll] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const reviewRef = useRef<HTMLDivElement>(null);

  const scans = useMemo(() => sheetsQ.data?.scans ?? [], [sheetsQ.data]);
  const detail = examDetailQ.data;
  const gradeId = detail?.school_grade_id ?? null;

  // The exam's audience: the grade's students, narrowed to the assigned sections when the exam has any.
  const studentsQ = useStudentsList({ grade: gradeId ?? undefined }, { enabled: gradeId != null });
  const rollsQ = useRollNumbers(gradeId);
  const roster = useMemo<RosterStudent[]>(() => {
    const rolls = new Map<number, number | null>();
    for (const r of rollsQ.data?.students ?? []) rolls.set(r.student_id, r.roll_number);
    const individual = detail?.assignment_scope === 'individual' ? new Set(detail.assigned_student_ids ?? []) : null;
    const sectionIds = !individual && detail?.assigned_section_ids?.length ? new Set(detail.assigned_section_ids) : null;
    return (studentsQ.data?.results ?? [])
      .filter((st) => (individual ? individual.has(st.id) : !sectionIds || (st.section != null && sectionIds.has(st.section.id))))
      .map((st) => ({
        id: st.id,
        name: personName(st) || st.username,
        section: st.section ? st.section.display || st.section.name : null,
        roll: rolls.get(st.id) ?? null,
      }));
  }, [studentsQ.data, rollsQ.data, detail]);
  const sectionOfStudent = useMemo(() => {
    const m = new Map<number, { id: number; name: string }>();
    for (const st of studentsQ.data?.results ?? []) if (st.section) m.set(st.id, { id: st.section.id, name: st.section.display || st.section.name });
    return m;
  }, [studentsQ.data]);
  const sectionOptions = useMemo(() => {
    const seen = new Map<number, string>();
    for (const st of studentsQ.data?.results ?? []) {
      if (!st.section) continue;
      if (roster.some((r) => r.id === st.id)) seen.set(st.section.id, st.section.display || st.section.name);
    }
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1], undefined, { numeric: true }));
  }, [studentsQ.data, roster]);
  const sectionKey = sectionOptions.some(([id]) => String(id) === section) ? section : 'all';

  // A section scopes the whole page. Sheets with no student yet belong to every scope.
  const inScope = (s: SheetRow) => sectionKey === 'all' || !s.student
    || String(sectionOfStudent.get(s.student.id)?.id) === sectionKey;
  const scoped = useMemo(() => scans.filter(inScope), [scans, sectionKey, sectionOfStudent]); // eslint-disable-line react-hooks/exhaustive-deps
  const scopedRoster = useMemo(
    () => (sectionKey === 'all' ? roster : roster.filter((r) => {
      const label = sectionOptions.find(([id]) => String(id) === sectionKey)?.[1];
      return r.section === label;
    })),
    [roster, sectionKey, sectionOptions],
  );
  const counts = useMemo(
    () => (sectionKey === 'all' ? sheetsQ.data?.counts : countSheets(scoped)),
    [sectionKey, sheetsQ.data, scoped],
  );
  const discardedCount = useMemo(() => scoped.filter((s) => s.status === 'discarded').length, [scoped]);
  const visibleRows = useMemo(() => scoped.filter((s) => matchesFilter(s, filter)), [scoped, filter]);
  const missing = useMemo(() => missingStudents(scopedRoster, scoped), [scopedRoster, scoped]);

  const subjectName = detail?.school_subject?.name ?? detail?.subject_name ?? detail?.subject ?? '—';
  const gradeName = detail?.school_grade_name ?? detail?.school_grade?.name ?? detail?.grade_name ?? detail?.grade ?? '—';
  const examName = sheetsQ.data?.exam?.name ?? null;

  // Sheets still being read: the only ones the processing list shows.
  const processing = useMemo(() => scans.filter((s) => s.status === 'queued' || s.status === 'reading'), [scans]);
  // A class-sized upload queues 40+ sheets: page the list.
  const processingPg = usePagination(processing, 10);
  const inTotal = scans.filter((s) => s.status !== 'discarded').length;

  // The review queue and the sheet being reviewed.
  // Sheets confirmed or discarded here leave the queue at once, before the list refetches.
  const [doneIds, setDoneIds] = useState<Set<number>>(() => new Set());
  const queue = useMemo(
    () => scoped.filter((s) => isReviewable(s.status) && !doneIds.has(s.id)).sort((a, b) => a.id - b.id),
    [scoped, doneIds]);
  // The sheet being reviewed stays pinned to the user's pick (a fix can turn it "matched" and take it out of
  // the queue, but it stays open until Confirm & next); with no pick it is the first queued sheet. A sheet
  // that is done (confirmed / discarded) is never kept open.
  const pinned = selectedId != null
    ? scans.find((s) => s.id === selectedId && !doneIds.has(s.id) && s.status !== 'confirmed' && s.status !== 'discarded')
    : undefined;
  const current = pinned ?? queue[0] ?? null;
  // Pin the default pick too, so fixing the first sheet doesn't swap it for the next one.
  useEffect(() => { if (selectedId == null && queue[0]) setSelectedId(queue[0].id); }, [selectedId, queue]);
  const queueShown = queueAll ? queue : queue.slice(0, QUEUE_PREVIEW);
  const needEyes = counts ? counts.needs_review + counts.duplicate + counts.error : 0;

  function openReview(row: SheetRow) {
    setSelectedId(row.id);
    const box = reviewRef.current;
    box?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    box?.focus({ preventScroll: true });
  }

  function reviewed() {
    if (current) setDoneIds((d) => new Set(d).add(current.id));
    const idx = queue.findIndex((s) => s.id === current?.id);
    const next = idx >= 0 ? (queue[idx + 1] ?? queue[idx - 1] ?? null) : (queue.find((s) => s.id !== current?.id) ?? null);
    setSelectedId(next ? next.id : null);
  }

  async function handleDownload() {
    if (examId == null) return;
    setDownloadError(null);
    setDownloading(true);
    try {
      await SchoolApi.downloadAnswerSheet(examId, sheetFilename(examName ?? 'exam'));
    } catch (err) {
      setDownloadError(apiErrorText(err, 'Could not download the answer sheet. Try again.'));
    } finally {
      setDownloading(false);
    }
  }

  const statTiles = counts ? [
    { label: 'Matched', value: counts.matched, help: 'matched' as const, hint: 'Ready to confirm' },
    { label: 'Needs review', value: counts.needs_review, help: 'needs_review' as const, hint: 'Unclear marks or roll', tone: counts.needs_review > 0 ? 'accent' as const : undefined },
    { label: 'Duplicates', value: counts.duplicate, help: 'duplicate_sheet' as const, hint: 'Same roll twice', tone: counts.duplicate > 0 ? 'accent' as const : undefined },
    { label: 'Errors', value: counts.error, help: 'sheet_status' as const, hint: 'Could not be read', tone: counts.error > 0 ? 'danger' as const : undefined },
    { label: 'Confirmed', value: counts.confirmed, help: 'sheet_status' as const, hint: 'Results written', tone: 'success' as const },
    { label: 'Discarded', value: discardedCount, help: 'sheet_status' as const, hint: 'Not counted', tone: 'muted' as const },
  ] : [];

  const tabs: Array<{ key: FilterKey; label: string; count: number }> = [
    { key: 'all', label: 'All', count: counts?.total ?? 0 },
    { key: 'needs_review', label: 'Needs review', count: counts?.needs_review ?? 0 },
    { key: 'matched', label: 'Matched', count: counts?.matched ?? 0 },
    { key: 'duplicate', label: 'Duplicate', count: counts?.duplicate ?? 0 },
    { key: 'error', label: 'Error', count: counts?.error ?? 0 },
    { key: 'queued', label: 'Queued / reading', count: (counts?.queued ?? 0) + (counts?.reading ?? 0) },
    { key: 'confirmed', label: 'Confirmed', count: counts?.confirmed ?? 0 },
    { key: 'discarded', label: 'Discarded', count: discardedCount },
  ];

  const audienceClause = roster.length > 0
    ? `${plural(roster.length, 'student')}${sectionOptions.length > 0 ? ` in ${joinNames(sectionOptions.map(([, n]) => n))}` : ''} \u00B7 `
    : '';

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        eyebrow={(
          <>
            <Link className="hover:text-ink" to="/exams/paper">Paper exams</Link>
            {[subjectName, gradeName].filter((v) => v && v !== '—').map((v) => ` · ${v}`).join('')}
          </>
        )}
        title={examName ?? 'Scan sheets'}
        subtitle={counts ? `${audienceClause}${plural(counts.total, 'sheet')} in \u00B7 ${needEyes} need your eyes before results go out.` : undefined}
        actions={(
          <>
            <Button
              variant="ghost"
              icon={<Download size={18} strokeWidth={1.8} />}
              loading={downloading}
              disabled={examId == null}
              onClick={handleDownload}>
              Answer sheet (PDF)
            </Button>
            <Button
              variant="ghost"
              icon={<BarChart3 size={18} strokeWidth={1.8} />}
              disabled={examId == null}
              onClick={() => navigate(`/exams/report/${examId}`)}>
              Exam report
            </Button>
            <Button
              icon={<Upload size={18} strokeWidth={1.8} />}
              disabled={examId == null}
              onClick={() => document.getElementById('drop')?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })}>
              Upload sheets
            </Button>
          </>
        )}
      />
      {downloadError && <p className="mb-3 text-xs text-danger">{downloadError}</p>}

      {sectionOptions.length > 0 && (
        <PanelCard size="filter" className="mb-[18px]">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[13px] text-muted">Section</span>
            <PillTabs<string>
              label="Filter by section" scroll height={36} value={sectionKey} onChange={setSection}
              options={[{ key: 'all', label: 'All sections' }, ...sectionOptions.map(([id, name]) => ({ key: String(id), label: name }))]} />
          </div>
        </PanelCard>
      )}

      {counts && (
        <PanelCard aria-label="Sheet counts" className="mb-[18px]">
          <StatStrip items={statTiles} columns={6} divided valueSize="lg" />
        </PanelCard>
      )}

      <div className={`mb-[18px] grid grid-cols-1 gap-[18px] ${roster.length > 0 ? 'min-[821px]:grid-cols-2 min-[1181px]:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]' : ''}`}>
        <PanelCard
          title="Upload filled sheets"
          subtitle="Marks are read automatically. Clean sheets are matched to students by roll number.">
          <DropZone disabled={examId == null} onUpload={(files) => uploadMut.mutateAsync(files)} />
          {processing.length > 0 && (
            <div className="mt-4">
              <div className="mb-1.5 flex justify-between text-[13px]">
                <span className="font-semibold text-ink">Processing queue</span>
                <span className="font-mono text-muted">{inTotal - processing.length} of {inTotal} read</span>
              </div>
              <Track value={inTotal > 0 ? ((inTotal - processing.length) / inTotal) * 100 : 0} label="Sheets read" />
              <ul aria-label="Processing queue" className="mt-1.5">
                {processingPg.pageItems.map((s) => (
                  <li key={s.id} className="flex items-center gap-3 border-b border-hairline py-2.5 last:border-b-0">
                    <span className="grid h-10 w-[30px] flex-none place-items-center rounded-[5px] border border-line-strong bg-surface text-control">
                      <FileCheck size={18} strokeWidth={1.8} aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-semibold text-ink">Sheet {`#${s.id}`}</span>
                      <span className="text-xs text-muted">{s.status === 'reading' ? 'Reading marks…' : `Waiting to be read · ${formatQueuedAt(s.created_at)}`}</span>
                    </span>
                    <Badge tone={s.status === 'reading' ? 'info' : 'neutral'}>{s.status === 'reading' ? 'Reading' : 'Queued'}</Badge>
                  </li>
                ))}
              </ul>
              {processingPg.pageCount > 1 && <PagedFooter from={processingPg.from} to={processingPg.to} total={processingPg.total} page={processingPg.page}
                pageCount={processingPg.pageCount} onPage={processingPg.setPage} label="Processing pagination" className="mt-2" />}
            </div>
          )}
        </PanelCard>
        {roster.length > 0 && <MissingCard roster={scopedRoster} missing={missing} />}
      </div>

      <div ref={reviewRef} tabIndex={-1} className="mb-[18px] scroll-mt-4 outline-none">
        <PanelCard
          title="Review sheets"
          subtitle="Only the marks the reader wasn't sure about. Click the right bubble on the sheet, then confirm."
          actions={<Badge tone="accent">{needEyes} waiting</Badge>}>
          {!current ? (
            <p className="py-2 text-sm text-muted">No sheets are waiting for review.</p>
          ) : (
            <ScanDrawer examId={examId ?? 0} scan={current} gradeId={gradeId} allScans={scans} onClose={reviewed}
              queue={(
                <div className="min-w-0">
                  <p className="mb-2 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Queue</p>
                  <ul className="flex flex-col gap-1">
                    {queueShown.map((s) => {
                      const on = s.id === current.id;
                      return (
                        <li key={s.id}>
                          <button
                            type="button" aria-pressed={on} onClick={() => setSelectedId(s.id)}
                            className={`flex w-full items-center gap-2.5 rounded-[12px] border p-2.5 text-left text-ink transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                              on ? 'border-line-strong bg-ground' : 'border-transparent bg-transparent'}`}>
                            <span className="grid h-10 w-[30px] flex-none place-items-center rounded-[5px] border border-line-strong bg-surface text-control">
                              <FileCheck size={18} strokeWidth={1.8} aria-hidden />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-[13px] font-semibold">{s.student?.name || 'Unknown student'} · #{s.id}</span>
                              <span className="block truncate text-xs text-muted">{s.warnings.map(warningLabel).join(' · ')}</span>
                            </span>
                            <Badge tone={KIND_TONE[s.status as keyof typeof KIND_TONE]}>{KIND_LABEL[s.status]}</Badge>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  {queue.length > QUEUE_PREVIEW && (
                    <ShowMoreFooter className="mt-2 px-2.5" shown={queueShown.length} total={queue.length} expanded={queueAll}
                      onToggle={() => setQueueAll((v) => !v)} showAllLabel={`Show all ${queue.length}`} />
                  )}
                </div>
              )} />
          )}
        </PanelCard>
      </div>

      <PanelCard
        title="Sheets & results"
        subtitle="Scores count once a sheet is confirmed. Pass mark 50%."
        help="answer_sheet"
        actions={(
          <Button
            variant="secondary"
            size="sm"
            icon={<Check size={18} strokeWidth={1.8} aria-hidden />}
            disabled={!counts || counts.matched === 0 || confirmMatchedMut.isPending}
            loading={confirmMatchedMut.isPending}
            onClick={() => setConfirmingAll(true)}>
            {counts && counts.matched > 0 ? `Confirm all matched (${counts.matched})` : 'Confirm all matched'}
          </Button>
        )}>
        <div className="mb-3.5">
          <PillTabs<FilterKey>
            label="Filter by status" scroll badge tabs height={36}
            value={filter} onChange={setFilter}
            options={tabs} />
        </div>

        {sheetsQ.isLoading ? (
          <div className="flex justify-center py-10"><Spinner size="md" /></div>
        ) : sheetsQ.isError ? (
          <ErrorPanel onRetry={() => sheetsQ.refetch()} />
        ) : visibleRows.length === 0 ? (
          <EmptyState
            icon={<FileCheck size={22} strokeWidth={1.8} />}
            title="No sheets here"
            body="Drop in photos or a PDF of the answer sheets above to get started."
          />
        ) : (
          <ScansTable
            rows={visibleRows}
            resetKey={`${filter}|${sectionKey}`}
            onReview={openReview}
            sectionOf={(id) => sectionOfStudent.get(id)?.name ?? null}
          />
        )}
      </PanelCard>

      <ConfirmDialog
        isOpen={confirmingAll}
        title="Confirm all matched sheets?"
        body={`This writes a result for ${counts?.matched ?? 0} matched sheet${counts?.matched === 1 ? '' : 's'}.`}
        confirmLabel="Confirm all"
        danger={false}
        onConfirm={() => { confirmMatchedMut.mutate(); setConfirmingAll(false); }}
        onCancel={() => setConfirmingAll(false)}
      />
    </div>
  );
}
